// /admin/second-accounts: pairs of accounts that look like the same person (for example a
// new free account made after reaching the 10-recipe limit). Nothing happens to anyone
// automatically: the admin looks at the clues and marks each pair.
import { Link } from 'react-router-dom'

import { getSecondAccounts, reviewPair } from '../../../api/adminV2'
import { useCounts } from '../AdminLayout'
import { useListParams } from '../list'
import { formatDate, formatNumber, LoadError, Loading, Pill, useFeedback, useLoad } from '../ui'

const TABS = [
  ['to_review', 'To review'],
  ['reviewed', 'Reviewed'],
]

const Account = ({ person, label }) => (
  <div className="a-pair-acct">
    <span className="a-label">{label}</span>
    <Link to={`/admin/people/${person.id}`} className="a-pair-name">
      {person.fullname || '(no name)'}
    </Link>
    <span className="a-muted a-small-text">{person.email}</span>
    <div className="a-btn-row">
      <Pill status={person.status_key} />
      {['paying', 'free_pro', 'lapsed'].includes(person.status_key) ? (
        <span className="a-small-text">{person.plan_label}</span>
      ) : null}
    </div>
    <dl className="a-pair-facts">
      <dt>Joined</dt>
      <dd>{formatDate(person.created_at)}</dd>
      <dt>Last active</dt>
      <dd>{formatDate(person.last_active)}</dd>
      <dt>Recipes</dt>
      <dd>
        {formatNumber(person.recipe_count)}
        {person.limit_reached_at ? ` · 10th on ${formatDate(person.limit_reached_at)}` : ''}
      </dd>
    </dl>
  </div>
)

const DECISION_TEXT = {
  same: 'Same person',
  different: 'Not the same person',
}

// One pair: both accounts side by side, the clues, and the buttons to mark it
export const PairCard = ({ pair, onChanged }) => {
  const { confirm, toast, toastError } = useFeedback()
  const { older, newer, review } = pair

  const mark = (decision) =>
    confirm({
      title: decision === 'same' ? 'Mark as the same person?' : 'Mark as different people?',
      text:
        decision === 'same'
          ? `This only records your decision. To act on it, open ${
              newer.fullname || 'the newer account'
            } and pause access or close the account.`
          : 'The pair leaves the to-review list. You can undo this under Reviewed.',
      action: decision === 'same' ? 'Same person' : 'Not the same person',
      initial: '',
      render: (note, setNote) => (
        <label className="a-field">
          <span className="a-field-label">Note for later (optional)</span>
          <textarea
            className="a-input"
            rows="2"
            maxLength="500"
            value={note}
            placeholder={decision === 'same' ? 'e.g. Emailed them on Oct 8' : 'e.g. Sisters'}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      ),
      run: (note) => reviewPair(older.id, newer.id, decision, note.trim() || null),
    }).then((ok) => {
      if (!ok) return
      toast(decision === 'same' ? 'Marked as the same person' : 'Marked as different people')
      onChanged && onChanged()
    })

  const undo = () =>
    reviewPair(older.id, newer.id, null)
      .then(() => {
        toast('Moved back to To review')
        onChanged && onChanged()
      })
      .catch((e) => toastError(e))

  return (
    <article className="a-panel a-pair">
      <div className="a-panel-head">
        <div className="a-btn-row">
          <Pill status={pair.strength} />
          {review ? <Pill status={review.decision} /> : null}
        </div>
        {review ? (
          <button type="button" className="a-btn a-small" onClick={undo}>
            Undo
          </button>
        ) : (
          <div className="a-btn-row">
            <button type="button" className="a-btn a-small" onClick={() => mark('different')}>
              Not the same person
            </button>
            <button type="button" className="a-btn a-small a-primary" onClick={() => mark('same')}>
              Same person
            </button>
          </div>
        )}
      </div>
      <div className="a-pair-grid">
        <Account person={older} label="Older account" />
        <Account person={newer} label="Newer account" />
      </div>
      <p className="a-pair-why-head">Why they look alike</p>
      <ul className="a-pair-why">
        {pair.reasons.map((r) => (
          <li key={r.clue}>{r.text}</li>
        ))}
      </ul>
      {review ? (
        <p className="a-muted a-small-text">
          {DECISION_TEXT[review.decision]}
          {review.reviewer ? `, marked by ${review.reviewer}` : ''}
          {review.at ? ` on ${formatDate(review.at)}` : ''}
          {review.note ? `: "${review.note}"` : ''}
        </p>
      ) : null}
    </article>
  )
}

// On a person's page: other accounts that may be the same person
export const PersonLookalikes = ({ personId, pairs }) => {
  if (!pairs || !pairs.length) return null
  const open = pairs.filter((pair) => pair.review?.decision !== 'different')
  if (!open.length) return null
  return (
    <section className="a-panel a-pair-note">
      <div className="a-panel-head">
        <h2>May be the same person as</h2>
        <Link className="a-btn a-small" to="/admin/second-accounts">
          Review
        </Link>
      </div>
      <div className="a-stack">
        {open.map((pair) => {
          const other = pair.older.id === Number(personId) ? pair.newer : pair.older
          return (
            <div key={pair.key}>
              <div className="a-btn-row">
                <Link to={`/admin/people/${other.id}`} className="a-pair-name">
                  {other.fullname || other.email}
                </Link>
                <span className="a-muted a-small-text">{other.email}</span>
                <Pill status={pair.strength} />
                {pair.review ? <Pill status={pair.review.decision} /> : null}
              </div>
              <p className="a-small-text">{pair.reasons.map((r) => r.text).join(' · ')}</p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

const SecondAccounts = () => {
  const { read, update } = useListParams({ tab: 'to_review' })
  const tab = read('tab') === 'reviewed' ? 'reviewed' : 'to_review'
  const { refreshCounts } = useCounts()
  const { data, error, loading, reload } = useLoad(getSecondAccounts, [])

  const changed = () => {
    reload()
    refreshCounts && refreshCounts()
  }
  const pairs = data?.[tab] || []

  return (
    <>
      <div className="a-head">
        <div>
          <h1>Second accounts</h1>
          <p className="a-sub">
            Accounts that look like the same person, for example a new free account made after
            reaching the 10-recipe limit. Nothing happens to anyone automatically: look at the clues
            and decide.
          </p>
        </div>
      </div>
      <div className="a-toolbar" role="group" aria-label="Show">
        {TABS.map(([key, label]) => (
          <button
            type="button"
            key={key}
            className="a-chip"
            aria-pressed={tab === key}
            onClick={() => update({ tab: key })}
          >
            {label}
            {data ? <span className="a-n">{formatNumber(data[key]?.length || 0)}</span> : null}
          </button>
        ))}
        {loading && data ? <span className="a-muted a-small-text">Loading…</span> : null}
      </div>
      {error && !data ? (
        <LoadError error={error} onRetry={reload} />
      ) : !data ? (
        <Loading />
      ) : pairs.length ? (
        <div className="a-pairs">
          {pairs.map((pair) => (
            <PairCard key={pair.key} pair={pair} onChanged={changed} />
          ))}
        </div>
      ) : (
        <section className="a-panel">
          <p className="a-empty">
            {tab === 'to_review'
              ? 'Nothing to review. No accounts look like the same person right now.'
              : 'No pairs reviewed yet.'}
          </p>
        </section>
      )}
      <section className="a-panel">
        <div className="a-panel-head">
          <h2>How accounts are matched</h2>
        </div>
        <p className="a-small-text">
          Each clue adds points; a pair shows up from 3 points, and 5 or more is a strong match.
          Same browser 3 · same phone number 3 · same full name 2 · very similar email 2 · 3 or more
          recipes with the same names 2 (5 or more: 3) · same internet connection 1 · the older
          account had reached the free limit before the newer one was made +1. People who share one
          Family Pro subscription are never matched. Browser and internet clues are collected from
          October 2026, when people sign in or open the app; Morsel keeps only a scrambled
          fingerprint, never the address itself.
        </p>
      </section>
    </>
  )
}

export default SecondAccounts
